
import React, { useRef, useCallback } from 'react';
import { Textarea } from '@/components/ui/textarea';
import GlassPill from '@/components/ui/GlassPill';

interface ResearchPromptFieldProps {
  value: string;
  onChange: (value: string) => void;
  researchType: string | null; // Now optional
}

const getPromptPlaceholder = (researchType: string | null) => {
  // Simple, concise example for research
  return 'e.g. AI trends in healthcare industry';
};

const ResearchPromptField: React.FC<ResearchPromptFieldProps> = React.memo(({
  value,
  onChange,
  researchType
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const cursorPosition = e.target.selectionStart;
    
    // Update value
    onChange(newValue);
    
    // Preserve cursor position after state update
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.setSelectionRange(cursorPosition, cursorPosition);
        textareaRef.current.focus();
      }
    }, 0);
  }, [onChange]);

  const wordCount = value.trim().split(/\s+/).filter(word => word.length > 0).length;
  const isValidLength = wordCount >= 1;

  return (
    <div className="space-y-3">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        placeholder={getPromptPlaceholder(researchType)}
        rows={4}
        className="resize-none"
        autoFocus={true}
      />
      
      <div className="flex items-center gap-2 mt-1">
        {wordCount < 1 && (
          <GlassPill 
            type="Min 1 word"
            variant="amber"
            className="text-xs py-0.5 px-2"
          />
        )}
        <GlassPill 
          type={`${wordCount} words`}
          variant={isValidLength ? "purple" : "gray"}
          className="text-xs py-0.5 px-2"
        />
        {isValidLength && (
          <GlassPill 
            type="Ready to generate!"
            variant="green"
            className="text-xs py-0.5 px-2"
          />
        )}
      </div>
    </div>
  );
});

ResearchPromptField.displayName = 'ResearchPromptField';

export default ResearchPromptField;
